import { Button } from 'primereact/button'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocalState } from '../../../../shared/UseLocalState'
import {
  ApiStartersPostRequest,
  ApiStartersValidatePostRequest,
  ExamDto,
  Horse,
  Person,
  ResponseError,
  ServiceDto,
  StarterExmsDto,
  StartersApi
} from '../../../../api'
import { actualShowState } from '../../../../state/ActualShow.state'
import { registrationState, ServiceDtoWithAmount } from '../Registration.state'
import { authState } from '../../../../state/Auth.state'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { appState } from '../../../../state/App.state'
import { Loading } from '../../../loading/Loading'

// JK/TODO: send req overlay on warnings with force

const validateIban = (value: string): boolean => {
  const raw = value.replace(/\s/g, '').toUpperCase()
  if (raw.length === 0) return false
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/.test(raw)) return false
  const rearranged = raw.slice(4) + raw.slice(0, 4)
  const numeric = rearranged.replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55))
  let remainder = 0
  for (const chunk of numeric.match(/.{1,9}/g) ?? []) {
    remainder = Number(String(remainder) + chunk) % 97
  }
  return remainder === 1
}

export const ClosingScreen = () => {
  /* STATE */
  const activeScreen = useLocalState<number>(registrationState.activeScreen$, registrationState.activeScreen$.value)
  const selectedHorse = useLocalState<Horse | null>(registrationState.selectedHorse$, registrationState.selectedHorse$.value)
  const selectedUser = useLocalState<Person | null>(registrationState.selectedRider$, registrationState.selectedRider$.value)
  const selectedExams = useLocalState<ExamDto[]>(registrationState.selectedExams$, registrationState.selectedExams$.value)
  const selectedServices = useLocalState<ServiceDtoWithAmount[]>(
    registrationState.selectedServices$,
    registrationState.selectedServices$.value
  )
  const gweGateExam = useLocalState<number>(registrationState.gweGateExam$, registrationState.gweGateExam$.value)
  const selectedStarter = useLocalState(registrationState.selectedStarter$, registrationState.selectedStarter$.value)
  const actualShowExams = useLocalState<ExamDto[]>(actualShowState.actualShowExams$, actualShowState.actualShowExams$.value)
  const booker = useLocalState<Person | null>(authState.user$, authState.user$.value)
  const scoringType = useLocalState<number | null>(registrationState.scoringType$, registrationState.scoringType$.value)

  /* DERIVED STATE */
  const ibanValid = useMemo(() => validateIban(booker?.bankAccountIBAN ?? ''), [booker?.bankAccountIBAN])
  const bankOwnerValid = useMemo(() => (booker?.bankOwner?.trim().length ?? 0) > 0, [booker?.bankOwner])

  const [errors, setErrors] = useState<string[]>([])
  const [warnings, setWarnings] = useState<string[]>([])
  const [validated, setValidated] = useState(false)
  const [loading, setLoading] = useState(false)
  const [choiceOfReinMap, setChoiceOfReinMap] = useState<Record<number, string>>({})
  const noChangesRef = React.useRef(false)
  const noChanges = useLocalState<boolean>(registrationState.noChanges$, registrationState.noChanges$.value)
  React.useEffect(() => { noChangesRef.current = noChanges }, [noChanges])

  /* LOGIC */
  const validateRegistration = useCallback(
    async (id: number) => {
      try {
        setLoading(true)
        setErrors([])
        setWarnings([])

        // get data for services request
        const showId = actualShowState.actualShowNr$.value
        const bookerId = authState.user$.value?.registerId

        // validate services request
        if (!showId) throw new Error(`showId is mandatory`)
        if (!bookerId) throw new Error('ownerId is mandatory')

        // get data for starter request
        const horseRegisterId = selectedHorse?.registerId ?? ''
        const riderRegisterId = selectedUser?.registerId ?? ''

        const bookedExams: StarterExmsDto[] =
          selectedExams
            ?.filter((exam) => !!exam?.id)
            ?.map((exam) => ({ examId: exam?.id, gweGateExam: id === exam?.id, choiceOfRein: exam?.choiceOfRein ? (choiceOfReinMap[exam.id!] ?? 'L') : undefined }) as unknown as StarterExmsDto) ??
          [] ??
          []

        // handle exams to cancel
        const starter = registrationState.selectedStarter$.value
        const examsToCancel: StarterExmsDto[] =
          (starter as any)?.bookedExams?.filter((e: StarterExmsDto) => !bookedExams?.some((e2) => e?.examId === e2?.examId)) ?? []
        examsToCancel?.forEach((e) =>
          bookedExams.push({ examId: e?.examId, gweGateExam: e?.gweGateExam, cancelRequest: true } as StarterExmsDto)
        )

        if (bookedExams.length < 1 && selectedServices.length < 1) {
          if (!registrationState.selectedStarter$.value) {
            setErrors([`Sie haben nichts gebucht`])
            setLoading(false)
            return { request: undefined, errors: [`Sie haben nichts gebucht`], warnings: [] }
          }
        }

        const originalServiceIds = new Set(
          (registrationState.originalServices$.value ?? []).map((s) => s.id)
        )
        const newServices = starter
          ? selectedServices?.filter((s) => !originalServiceIds.has(s.id))
          : selectedServices

        const request: ApiStartersValidatePostRequest = {
          starterDto: {
            bookerId,
            showId,
            startNo: starter?.startNo,
            horseRegisterId,
            riderRegisterId,
            bookedExams,
            bookedServices: newServices?.map((service) => ({ serviceId: service.id, quantity: service?.amount })) ?? [],
            scoringType: registrationState.scoringType$.value ?? undefined
          }
        }
        // validate starter request at backend
        return new Promise<{ request: ApiStartersPostRequest | undefined; warnings: string[]; errors: string[] }>((resolve) =>
          new StartersApi(authState.apicfg())
            .apiStartersValidatePost(request)
            .then(() => {
              registrationState.noChanges$.next(false)
              resolve({
                request,
                errors: [],
                warnings: []
              })
              setLoading(false)
            })
            .catch((err: ResponseError) => {
              try {
                err.response
                  .json()
                  .then((resp: { errors: string[]; warnings: [] }) => {
                    const isNoChangesWarning = [...(resp?.warnings ?? []), ...(resp?.errors ?? [])].some((w: string) =>
                      w?.toLowerCase().includes('keine') || w?.toLowerCase().includes('no change') || w?.toLowerCase().includes('änderung')
                    )
                    if (isNoChangesWarning) {
                      registrationState.noChanges$.next(true)
                      setErrors([])
                      setWarnings(resp?.warnings ?? resp?.errors ?? [])
                    } else {
                      registrationState.noChanges$.next(false)
                      setErrors(resp?.errors ?? [])
                      setWarnings(resp?.warnings ?? [])
                    }
                    resolve({ request, errors: isNoChangesWarning ? [] : (resp?.errors ?? []), warnings: resp?.warnings ?? [] })
                    setLoading(false)
                  })
                  .catch(() => {
                    setErrors([`Etwas lief schief!`])
                    setWarnings([])
                    resolve({ request, errors: [`Etwas lief schief!`], warnings: [] })
                    setLoading(false)
                  })
              } catch (err) {
                setErrors([`Etwas lief schief!`])
                setWarnings([])
                resolve({ request, errors: [`Etwas lief schief!`], warnings: [] })
                setLoading(false)
              }
            })
        )
      } catch (err) {
        console.error(err)
        setErrors(['Etwas lief schief'])
        setLoading(false)
      }
    },
    [selectedHorse, selectedUser, selectedExams, selectedServices, choiceOfReinMap]
  )

  const finishRegistration = useCallback(async (id: number) => {
    setLoading(true)
    const validation = await validateRegistration(id)
    const isNoChanges = noChangesRef.current
    const hasNoErrors = (validation?.errors ?? []).length < 1
    if ((hasNoErrors || isNoChanges) && validation?.request) {
      setLoading(true)
      new StartersApi(authState.apicfg())
        .apiStartersPostRaw(validation?.request)
        .then(() => {
          appState.addToast('Anmeldung erfolgreich!', 'success')
          window.location.hash = '#/home'
        })
        .catch(() => {
          appState.addToast('Anmeldung fehlgeschlagen!', 'error')
        })
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [validateRegistration])

  const handleGweGateExamChange = useCallback((examId: number) => {
    registrationState.gweGateExam$.next(examId)
    validateRegistration(examId).then(() => setValidated(true))
  }, [validateRegistration])

  const handleChoiceOfReinChange = useCallback((examId: number) => {
    setChoiceOfReinMap((prev) => ({ ...prev, [examId]: prev[examId] === 'L' ? 'R' : 'L' }))
  }, [])

  const cancelledExams: ExamDto[] = useMemo(() => {
    const starter = registrationState.selectedStarter$.value as any
    const bookedExamIds: number[] = starter?.bookedExams?.map((e: StarterExmsDto) => e?.examId) ?? []
    const selectedExamIds = selectedExams.map((e) => e.id)
    const cancelledIds = bookedExamIds.filter((id) => !selectedExamIds.includes(id))
    return cancelledIds
      .map((id) => actualShowExams.find((e) => e.id === id))
      .filter((e): e is ExamDto => !!e)
  }, [selectedExams, actualShowExams])

  const combinedData = [
    ...selectedExams.map((exam) => ({
      exam,
      starterExams: selectedExams.find((se) => se.id === exam.id) || { gweGateExam: false },
      cancel: false
    })),
    ...cancelledExams.map((exam) => ({
      exam,
      starterExams: { gweGateExam: false },
      cancel: true
    }))
  ]

  /* EFFECTS */
  useEffect(() => {
    if (activeScreen === 5) {
      // choiceOfRein aus gespeichertem Starter übernehmen, sonst Default 'L'
      const starter = registrationState.selectedStarter$.value as any
      const defaults: Record<number, string> = {}
      selectedExams.forEach((exam) => {
        if (exam?.choiceOfRein && exam?.id != null) {
          const existing = starter?.bookedExams?.find((e: any) => e?.examId === exam.id)
          defaults[exam.id] = existing?.choiceOfRein ?? choiceOfReinMap[exam.id] ?? 'L'
        }
      })
      setChoiceOfReinMap(defaults)
      setValidated(false)
      validateRegistration(gweGateExam).then(() => setValidated(true))
    }
  }, [activeScreen])

  // Re-validierung bei Änderung der Hand-Auswahl
  const choiceOfReinMapRef = React.useRef(choiceOfReinMap)
  useEffect(() => {
    if (choiceOfReinMapRef.current === choiceOfReinMap) return
    choiceOfReinMapRef.current = choiceOfReinMap
    if (activeScreen === 5) {
      setValidated(false)
      validateRegistration(gweGateExam).then(() => setValidated(true))
    }
  }, [choiceOfReinMap])

  /* TEMPLATE */
  return (
    <>
      <Loading loading={loading} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <h2>Zusammenfassung</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <p>
            <b>Pferd</b>
            <br />
            <span>{selectedHorse?.name}</span>
          </p>
          <p>
            <b>Reiter</b>
            <br />
            <span>
              {selectedUser?.firstName} {selectedUser?.lastName} {selectedUser?.member}
            </span>
            {scoringType != null && (
              <>
                <br />
                <span style={{ fontStyle: 'italic', color: '#555' }}>
                  Wertung: {[{ label: 'Jugendwertung A', value: 3 }, { label: 'Jugendwertung B', value: 4 }, { label: 'Freizeitwertung', value: 2 }, { label: 'Sportreiterwertung', value: 1 }].find((o) => o.value === scoringType)?.label ?? scoringType}
                </span>
              </>
            )}
          </p>
          {<b>Services</b>}

          {/* Allgemeine Services (Typ G = Grundgebühr Reiter) */}
          {selectedServices?.some((s) => !s?.type || s?.type === 'G') && (
            <>
              <p style={{ margin: '0.25rem 0', fontStyle: 'italic', color: '#555' }}>Allgemein (Reiter)</p>
              <DataTable
                showHeaders={true}
                value={selectedServices
                  ?.filter((s) => !s?.type || s?.type === 'G')
                  ?.sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''))}
                emptyMessage='keine Services'
              >
                <Column header='Bezeichnung' body={(service: ServiceDto) => service?.name} />
                <Column header='Menge' body={(s: ServiceDtoWithAmount) => s?.amount} />
                <Column
                  header='Preis'
                  body={(service: ServiceDtoWithAmount) =>
                    ((service?.price ?? 0) * (service?.amount ?? 0))?.toFixed(2).replace('.', ',') + '\u00a0€'
                  }
                />
              </DataTable>
            </>
          )}

          {/* Startnummer-Services (Typ N, H, I) – neue Buchung */}
          {selectedServices?.some((s) => s?.type === 'N' || s?.type === 'H' || s?.type === 'I') && (
            <div style={{ marginTop: '0.5rem' }}>
              <p style={{ margin: '0.25rem 0', fontStyle: 'italic', color: '#555' }}>Startnummer-Services</p>
              <DataTable
                showHeaders={true}
                value={selectedServices
                  ?.filter((s) => s?.type === 'N' || s?.type === 'H' || s?.type === 'I')
                  ?.sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''))}
                emptyMessage='keine Services'
              >
                <Column header='Bezeichnung' body={(service: ServiceDto) => service?.name} />
                <Column header='Menge' body={(s: ServiceDtoWithAmount) => s?.amount} />
                <Column
                  header='Preis'
                  body={(service: ServiceDtoWithAmount) =>
                    ((service?.price ?? 0) * (service?.amount ?? 0))?.toFixed(2).replace('.', ',') + '\u00a0€'
                  }
                />
              </DataTable>
            </div>
          )}

          {/* Zusatzleistungen (Typ Z) */}
          {selectedServices?.some((s) => s?.type === 'Z') && (
            <div style={{ marginTop: '0.5rem' }}>
              <p style={{ margin: '0.25rem 0', fontStyle: 'italic', color: '#555' }}>Zusatzleistungen</p>
              <DataTable
                showHeaders={true}
                value={selectedServices
                  ?.filter((s) => s?.type === 'Z')
                  ?.sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''))}
                emptyMessage='keine Services'
              >
                <Column header='Bezeichnung' body={(service: ServiceDto) => service?.name} />
                <Column header='Menge' body={(s: ServiceDtoWithAmount) => s?.amount} />
                <Column
                  header='Preis'
                  body={(service: ServiceDtoWithAmount) =>
                    ((service?.price ?? 0) * (service?.amount ?? 0))?.toFixed(2).replace('.', ',') + '\u00a0€'
                  }
                />
              </DataTable>
            </div>
          )}

          {selectedServices?.length < 1 && <p style={{ color: '#888' }}>keine Services</p>}

          <b>Prüfungen</b>
          <DataTable showHeaders={true} value={combinedData} emptyMessage='keine Prüfungen'
            rowClassName={(rowData: { exam: ExamDto; cancel: boolean }) => rowData.cancel ? 'exam-cancelled' : ''}
          >
            <Column header='Nr' body={(rowData: { exam: ExamDto }) => rowData.exam?.no} bodyStyle={{ width: '3rem' }} />
            <Column header='Prüfung' body={(rowData: { exam: ExamDto; cancel: boolean }) => (
              <span style={rowData.cancel ? { textDecoration: 'line-through', color: '#888' } : {}}>
                {rowData.exam?.name}{rowData.exam?.type ? ` (${rowData.exam.type})` : ''}
              </span>
            )} />
            <Column header='GWE Typ' body={(rowData: { exam: ExamDto; cancel: boolean }) => rowData.exam?.gweType} />
            <Column
              header='GWE Gang Auswahl'
              body={(rowData: { exam: ExamDto; starterExams: StarterExmsDto; cancel: boolean }) =>
                !rowData.cancel && rowData.exam?.gweType === 'G' ? (
                  <input
                    type='radio'
                    name='gweGateExam'
                    style={{ cursor: 'pointer' }}
                    checked={rowData?.exam?.id === gweGateExam}
                    onClick={(e) => handleGweGateExamChange(rowData.exam.id ?? 0)}
                  />
                ) : null
              }
            />
            <Column
              header='Hand'
              body={(rowData: { exam: ExamDto; cancel: boolean }) => {
                if (rowData.cancel || !rowData.exam?.choiceOfRein || rowData.exam?.id == null) return null
                const val = choiceOfReinMap[rowData.exam.id] ?? 'L'
                return (
                  <span
                    style={{ cursor: 'pointer', fontWeight: 'bold', userSelect: 'none', padding: '0.2rem 0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    onClick={() => handleChoiceOfReinChange(rowData.exam.id!)}
                  >
                    {val}
                  </span>
                )
              }}
            />
            <Column
              header='Preis'
              body={(rowData: { exam: ExamDto; cancel: boolean }) => {
                const priceStr = (selectedUser?.member
                  ? rowData.exam?.price?.toFixed(2).replace('.', ',')
                  : rowData.exam?.priceNoMember?.toFixed(2).replace('.', ',')) + '\u00a0€'
                return (
                  <span style={rowData.cancel ? { textDecoration: 'line-through', color: '#888' } : {}}>
                    {priceStr}
                  </span>
                )
              }}
            />
          </DataTable>
        </div>
        <div>
          {(warnings ?? [])?.map((w, i) => (
            <p key={i} style={{ color: '#e15501', fontWeight: 'bold' }}>
              {w}
            </p>
          ))}
        </div>
        <div>
          {(errors ?? [])?.map((e, i) => (
            <p key={i} style={{ color: '#e74c3c', fontWeight: 'bold' }}>
              {e}
            </p>
          ))}
        </div>
        {!ibanValid && (
          <p style={{ color: '#e74c3c', fontWeight: 'bold' }}>
            Bitte hinterlegen Sie eine gültige IBAN in Ihren Kontodaten (Adressänderung).
          </p>
        )}
        {!bankOwnerValid && (
          <p style={{ color: '#e74c3c', fontWeight: 'bold' }}>
            Bitte hinterlegen Sie einen Kontoinhaber in Ihren Kontodaten (Adressänderung).
          </p>
        )}
        <br />
        <Button className='button' onClick={() => finishRegistration(gweGateExam)} disabled={(!noChanges && (errors?.length > 0 || !validated)) || !ibanValid || !bankOwnerValid}>
          {noChanges
            ? `Änderungen speichern und Meldebestätigung für Startnummer ${selectedStarter?.startNo} nochmals verschicken`
            : selectedStarter
              ? `Startnummer ${selectedStarter.startNo} kostenpflichtig aktualisieren..`
              : `Zahlungspflichtig buchen – Abbuchung ca. 14 Tage vor dem Event`}
          {!noChanges && warnings?.length > 0 && errors?.length < 1 ? ` (Trotz Warnungen)` : null}
        </Button>
      </div>
    </>
  )
}
